import React from 'react';
import { FaCheckCircle } from 'react-icons/fa';
import Lottie from 'lottie-react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

// Lottie animations
import busTrackingAnim from '../../assets/icons/school_bus.json';
import parentAppAnim from '../../assets/icons/books.json';
import teacherAppAnim from '../../assets/icons/enroll.json';
import notificationsAnim from '../../assets/icons/message-sending.json';
import websiteAnim from '../../assets/icons/school_info.json';
import simpleUxAnim from '../../assets/icons/Animation - 1739896027009.json';
import secureCloudAnim from '../../assets/icons/infinate_loader.json';
import windowsAppAnim from '../../assets/icons/school_info.json';

/* --------------------- Updated Styled Components --------------------- */
const Section = styled.section`
  padding: 2.4rem 1rem;
  background: linear-gradient(180deg, #f8fbff 0%, #eef4ff 100%);
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 1.3rem;
`;

const Title = styled.h2`
  font-size: 2.1rem;
  color: #0f172a;
  margin-bottom: 0.9rem;
  font-weight: 700;
  letter-spacing: -0.02em;
`;

const Description = styled.p`
  font-size: 1rem;
  color: #334155;
  line-height: 1.7;
  margin-bottom: 1rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const FreeHighlight = styled.div`
  color: #0f766e;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(15, 118, 110, 0.12);
  padding: 0.6rem 1rem;
  border-radius: 9999px;
  margin-top: 1rem;
  font-size: 1.1rem;

  svg {
    margin-right: 0.75rem;
    font-size: 1.2rem;
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.1rem;
  justify-items: center;
  padding-left: 0.3rem;
  padding-right: 0.3rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Card = styled(motion.div)`
  background: linear-gradient(160deg, #ffffff 0%, #f7fbff 100%);
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
  padding: 1.1rem;
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  width: 100%;
  max-width: 360px;
`;

const LottieWrapper = styled.div`
  width: 110px;
  height: 110px;
  margin: 0 auto 0.8rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.18rem;
  color: #0f172a;
  margin-bottom: 0.45rem;
  font-weight: 600;
`;

const FeatureDescription = styled.p`
  font-size: 0.9rem;
  color: #475569;
  line-height: 1.6;
`;

// Animation Variants
const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
  hover: {
    scale: 1.05,
    transition: { duration: 0.2 },
  },
};


const FeaturesSection = () => {
  const featuresData = [
    {
      title: 'Bus Tracking',
      description: 'Real-time tracking of school buses for enhanced safety and parent communication.',
      lottieData: busTrackingAnim,
    },
    {
      title: 'Parent Application',
      description: 'Mobile app for parents to stay connected & informed about their child’s school activities.',
      lottieData: parentAppAnim,
    },
    {
      title: 'Teacher Application',
      description: 'Empower teachers with tools for efficient classroom management and communication.',
      lottieData: teacherAppAnim,
    },
    {
      title: 'Free Notifications',
      description: 'Keep parents instantly updated with important school announcements at no cost.',
      lottieData: notificationsAnim,
    },
    {
      title: 'Free School Website',
      description: 'A professional online presence with a complimentary website for your school.',
      lottieData: websiteAnim,
    },
    {
      title: 'Simple UX',
      description: 'Intuitive, user-friendly design for seamless navigation across devices.',
      lottieData: simpleUxAnim,
    },
    {
      title: 'Secure Cloud System',
      description: 'Reliable, secure cloud infrastructure to protect and manage school data anytime.',
      lottieData: secureCloudAnim,
    },
    {
      title: 'Windows Application',
      description: 'An accessible, functional application designed for Windows environments.',
      lottieData: windowsAppAnim,
    },
    {
      title: 'Attendance Automation',
      description: 'Track class-wise attendance with role-based controls and instant parent visibility.',
      lottieData: parentAppAnim,
    },
    {
      title: 'Exam & Results',
      description: 'Create exams, enter marks, publish results, and share report cards with less manual work.',
      lottieData: teacherAppAnim,
    },
    {
      title: 'Fee & Finance Controls',
      description: 'Configure fee templates, dues, waivers, and payment reminders from one dashboard.',
      lottieData: notificationsAnim,
    },
    {
      title: 'School Website Builder',
      description: 'Launch and manage your school website with editable banners, notices, and SEO-ready content.',
      lottieData: websiteAnim,
    },
  ];

  return (
    <Section id="features-section">
      <Header>
        <Title>Key Features to Simplify and Scale Your School Management</Title>
        <Description>
          Admissions, academics, attendance, fees, exams, communication, transport, and website management in one AI-powered platform.
          <FreeHighlight>
            <FaCheckCircle />
            Free for schools with up to 500 students!
          </FreeHighlight>
        </Description>
      </Header>

      <GridContainer>
        {featuresData.map((feature, index) => (
          <Card
            key={index}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            whileHover="hover"
          >
            <LottieWrapper>
              <Lottie animationData={feature.lottieData} loop autoplay />
            </LottieWrapper>
            <FeatureTitle>{feature.title}</FeatureTitle>
            <FeatureDescription>{feature.description}</FeatureDescription>
          </Card>
        ))}
      </GridContainer>

    </Section>
  );
};

export default FeaturesSection;
